// Quest markers (review R4-003, 2026-10-15: "Quest NPCs have no marker or pointer, and Rocco is hard to see behind the
// back-bar walls"). The server appends to S2C.Quests every NPC with something for you: '!' a quest on offer, '?' a
// step that sends you to talk to them. Drawn four ways:
//   over the head  a bobbing gold '!' or '?' with the name under it, in the layer above the night lightmap and the
//                  walls, so Rocco behind the bar shows through them;
//   the minimap    a gold pip, held on the rim with a notch when the NPC is off it;
//   the big map    a gold pip (handed to BigMap.draw with the friends);
//   the tracker    the quest's line says who and where ("ROCCO: the Tavern, behind the bar").
import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { R, Kind } from '../../shared/src/protocol';
import { CELL_PX } from '../../shared/src/iso';
import { drawText, textWidth, textHeight } from './pixfont';

export interface QMark { id: number; kind: number; q: number; name: string; where: string; map: number; x: number; y: number }
const GOLD = '#ffd38a', INK = '#0f0e22';

function glyph(ch: string): Texture {
  // a gold tag with the mark in ink, 11 x 13 px: drawn by code, no art
  const c = document.createElement('canvas'); c.width = 11; c.height = 13; const g = c.getContext('2d')!;
  g.fillStyle = INK; g.fillRect(0, 0, 11, 13); g.fillStyle = GOLD; g.fillRect(1, 1, 9, 11);
  g.fillStyle = INK;
  if (ch === '!') { g.fillRect(4, 2, 3, 6); g.fillRect(4, 9, 3, 2); }
  else { g.fillRect(3, 2, 5, 2); g.fillRect(7, 3, 2, 3); g.fillRect(5, 5, 3, 2); g.fillRect(4, 6, 2, 2); g.fillRect(4, 9, 2, 2); }
  return Texture.from(c);
}
function label(t: string): Texture {
  // R3-033: a full 1 px ink outline round the gold name, not a drop shadow only ('PLAYMAKER ROCCO' was lost on cream brick)
  const s = t.toUpperCase().slice(0, 24); const c = document.createElement('canvas'); c.width = textWidth(s, 1, 'whyte') + 3; c.height = textHeight(1, 'whyte') + 3;
  const g = c.getContext('2d')!;
  for (const [dx, dy] of [[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1], [2, 2]]) drawText(g, s, 1 + dx, 1 + dy, INK, 1, undefined, 'whyte');
  drawText(g, s, 1, 1, GOLD, 1, undefined, 'whyte'); return Texture.from(c);
}

export class QuestMarks {
  root = new Container();
  marks: QMark[] = [];
  private tags = new Map<number, Container>();
  private tex: Record<string, Texture> = {};

  /** the appended block of S2C.Quests (ui.ts calls this after the quests and the photo count) */
  read(r: R) {
    this.marks = [];
    if (r.left <= 0) return;
    const n = r.u8();
    for (let i = 0; i < n; i++) this.marks.push({ id: r.u32(), kind: r.u8(), q: r.u8(), name: r.str(), where: r.str(), map: r.u16(), x: r.pos(), y: r.pos() });
    for (const [key, c] of this.tags) if (!this.marks.some(m => m.id * 4 + m.kind === key)) { c.destroy({ children: true }); this.tags.delete(key); }   // gone, or '!' became '?'
  }
  /** the tracker's "who and where" line for quest index qi, or '' */
  whereFor(qi: number): string { const m = this.marks.find(k => k.q === qi && k.kind === 2); return m ? `${m.name}: ${m.where}` : ''; }

  /** every frame: a tag over each marked NPC you can see (views are main.ts's interpolated entities) */
  update(views: Map<number, { e: { kind: number }; spr: Container }>, t: number, hud?: DOMRect[], css = 0, edges: number[][] = [], me?: { x: number; y: number }) {
    const placed: [number, number][] = [];   // (2026-10-29: two people a couple of metres apart at the bar put their tags on top of each other)
    for (const m of this.marks) {
      const v = views.get(m.id);
      const key = m.id * 4 + m.kind; let c = this.tags.get(key);
      // (an NPC not drawn - outside the building you are in, indoors being its own view - takes its tag with it)
      if (!v || v.e.kind !== Kind.Npc || !v.spr.visible) { if (c) c.visible = false; continue; }
      if (!c) {
        const k = m.kind === 1 ? '!' : '?';
        c = new Container();
        const gl = new Sprite(this.tex[k] ??= glyph(k)); gl.anchor.set(0.5, 1);
        const nm = new Sprite(label(m.name)); nm.anchor.set(0.5, 0); nm.y = 1;
        c.addChild(gl, nm); this.root.addChild(c); this.tags.set(key, c);
      }
      c.visible = true;
      const h = 44;                                            // a figure's head height (spr.height counts the shadow and the gear)
      c.x = Math.round(v.spr.x); c.y = Math.round(v.spr.y - h - 8 + Math.sin(t / 260 + m.id) * 2);
      for (let k = 0; k < 4 && placed.some(([x, y]) => Math.abs(x - c!.x) < 48 && Math.abs(y - c!.y) < 18); k++) c.y -= 18;   // a tag that would sit on another steps up
      placed.push([c.x, c.y]);
      if (css) {
        const cs = c.worldTransform.a || 1, [gl, nm] = c.children; gl.x = 0; nm.x = 0;
        // R10-040: a tag never sits on YOUR figure (an NPC just below you put its '!' on your foot): like the names, the part
        // that meets your box (main.ts __dm.feet, HEAD_PX 54, inflated 4 px) steps sideways off it, the shorter way
        const ft = (window as any).__dm?.feet as { x: number; y: number; k: number } | undefined;
        if (ft) for (const part of [gl, nm]) { part.visible = true; const b = part.getBounds(), x0 = b.x * css, y0 = b.y * css, x1 = (b.x + b.width) * css, y1 = (b.y + b.height) * css;
          const m0 = ft.x - 16 * ft.k - 4, m1 = ft.x + 16 * ft.k + 4, t0 = ft.y - 54 * ft.k - 4, t1 = ft.y + 4;
          if (x1 > m0 && x0 < m1 && y1 > t0 && y0 < t1) part.x += Math.round((x1 - m0 < m1 - x0 ? m0 - x1 : m1 - x0) / css / cs); }
        // R3-025: the name stays on the screen (PLAYMAKER ROCCO was cut at the left edge at HUGE): it slides along under its mark
        const nb = nm.getBounds(), n0 = nb.x * css, n1 = (nb.x + nb.width) * css;
        if (n0 < 4) nm.x += Math.round((4 - n0) / css / cs); else if (n1 > innerWidth - 4) nm.x -= Math.round((n1 - innerWidth + 4) / css / cs);
      }
      // R10-020: the HUD wins over a world label. A part of the tag (the mark, the name) whose box meets a HUD box on
      // screen is not drawn; css: canvas px -> CSS px. R3-036: so does a threat chevron at the screen edge (one sat on the P
      // of PLAYMAKER ROCCO in the Tavern on an iPhone 15): its box comes from main.ts as it draws it (edges, canvas px, this
      // frame's), never read back from anything
      if (hud && css) {
        const clear = (part: Container) => { part.visible = true; const b = part.getBounds();
          const x0 = b.x * css, y0 = b.y * css, x1 = (b.x + b.width) * css, y1 = (b.y + b.height) * css;
          return y0 >= 0 && !hud.some(r => x1 > r.left && x0 < r.right && y1 > r.top && y0 < r.bottom)
            && !edges.some(e => x1 > e[0] * css && x0 < e[2] * css && y1 > e[1] * css && y0 < e[3] * css); };
        const [gl, nm] = c.children as Container[]; nm.y = 1;
        gl.visible = clear(gl);
        // the name under its mark, else (R3-025: kept on the screen at HUGE it met the REST prompt) over it, else not at all
        if (!clear(nm)) { nm.y = -gl.height - nm.height - 1; if (!gl.visible || !clear(nm)) {
          // R3 round 12 (on R3-036): before it goes, the name slides sideways, under its mark and then over it: at the iPhone 15
          // portrait spawn, story card folded, a threat chevron at the edge took PLAYMAKER ROCCO's name away whole when 30-odd
          // px inward cleared it. The smallest slide in 6 px steps that is on screen, clear of every HUD box and chevron, and
          // still under its mark (the mark's middle inside the name's span), else not at all. Arithmetic on one bounds read
          // per position, and only for a tag that has already met something, so a clear frame costs nothing more.
          const cs = c.worldTransform.a || 1, gv = gl.visible; gl.visible = true; const gb = gl.getBounds(), mx = (gb.x + gb.width / 2) * css; gl.visible = gv;   // (a hidden mark measures as nothing)
          let dx: number | null = null;
          for (const y of gl.visible ? [1, -gl.height - nm.height - 1] : [1]) {
            nm.y = y; nm.visible = true; const b = nm.getBounds(), x0 = b.x * css, w = b.width * css, y0 = b.y * css, y1 = (b.y + b.height) * css;
            const ok = (s: number) => { const a = x0 + s, z = a + w; return a >= 4 && z <= innerWidth - 4 && y0 >= 0 && mx > a && mx < z
              && !hud.some(r => z > r.left && a < r.right && y1 > r.top && y0 < r.bottom) && !edges.some(e => z > e[0] * css && a < e[2] * css && y1 > e[1] * css && y0 < e[3] * css); };
            for (let d = 6; d <= w / 2 && dx === null; d += 6) for (const s of [-d, d]) if (ok(s)) { dx = s; break; }
            if (dx !== null) break;
          }
          if (dx !== null) nm.x += Math.round(dx / css / cs); else { nm.y = 1; nm.visible = false; }
        } }
      }
      // hud2 (cycle 001): the mark always, its name only within 6 m (48 world px) of you
      if (me) { const vv = v as unknown as { nx?: number; ny?: number }; const nm = c.children[1];
        if (nm && Math.hypot((vv.nx ?? 0) - me.x, (vv.ny ?? 0) - me.y) > 48) nm.visible = false; }
    }
  }
  /** on the minimap (cells: you at cx, cy; k px per cell; R2 cells each way; W px wide), on map id mapId */
  drawMini(g: CanvasRenderingContext2D, cx: number, cy: number, k: number, R2: number, W: number, mapId: number) {
    for (const m of this.marks) {
      if (m.map !== mapId) continue;
      let x = (m.x / CELL_PX - cx + R2) * k, y = (m.y / CELL_PX - cy + R2) * k;
      const dx = x - W / 2, dy = y - W / 2, d = Math.hypot(dx, dy), rim = W / 2 - 7;
      const off = d > rim; if (off) { x = W / 2 + dx / d * rim; y = W / 2 + dy / d * rim; }   // off the map: held on the rim
      g.fillStyle = INK; g.fillRect(x - 4, y - 4, 8, 8); g.fillStyle = m.kind === 2 ? GOLD : '#ffa94d'; g.fillRect(x - 3, y - 3, 6, 6);
      if (off) { g.fillStyle = INK; g.fillRect(x - 1, y - 1, 2, 2); }
    }
  }
  /** for BigMap.draw: the street's marks as pips */
  street(): { x: number; y: number; c: string; label?: string; goal?: boolean }[] { return this.marks.filter(m => m.map === 0).map(m => ({ x: m.x, y: m.y, c: m.kind === 2 ? GOLD : '#ffa94d', label: m.kind === 2 ? m.name : undefined, goal: true })); }   // (studio 009f: named on the big map, which frames you and them)
}
